import { Router } from '@angular/router';
import { CourseService } from '../../services/courses.service';
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import * as actions from './set-course.actions';
import * as CourseWorkbookActions from '../course-workbook/course-workbook.actions';

import { SubscriptionService } from '../../services/subscription.service';
import { isAllCourseWorkbooksLoaded, selectCourseData } from '..';
import { GetAllLessons, GetAllLessonSteps, ClearAllLessons } from '../lesson/lesson.actions';
import * as reducers from '../index'; // Import all of '..', assuming new selectors are exported there

@Injectable()
export class SetCourseEffect {
  constructor(
      private actions$: Actions,
      private store$: Store,
      private courseService: CourseService,
      private subscriptionService: SubscriptionService,
      private router: Router,
      private store: Store
  ) {}

  setCurriculumCourse$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(actions.SetCourseActionTypes.SetCurriculumCourse),
        switchMap((action: any) => {
          const courseId = action?.payload?.course?._id;
          if (!courseId) {
            // Optionally, dispatch a fail action or log an error
            // For now, returning an empty observable or an observable of a fail action
            return of(new actions.SetCurriculumCourseFail('Course ID missing in SetCurriculumCourse action'));
          }

          return of(action).pipe(
            withLatestFrom(
              this.store.select(isAllCourseWorkbooksLoaded, { id: courseId }),
              this.store.select(reducers.areAllLessonsLoadedForCourse, { id: courseId }),
              this.store.select(reducers.areAllLessonStepsLoadedForCourse, { id: courseId })
            ),
            tap(([dispatchAction, workbooksLoaded, lessonsLoaded, stepsLoaded]) => {
              // Conditional dispatch for CourseWorkbooks
              // The getAllCourseWorkbooks$ effect now has its own filter, so we can dispatch this.
              // It will only make an API call if its internal filter passes.
              if (!workbooksLoaded) { // This condition means courseId was not state.selectedId in courseWorkbook state
                  this.store$.dispatch(new CourseWorkbookActions.GetAllCourseWorkbooks(courseId));
              }

              // Conditional dispatch for Lessons
              if (!lessonsLoaded) {
                this.store$.dispatch(new ClearAllLessons(null)); // Clear if we are about to fetch new ones
                this.store$.dispatch(new GetAllLessons(courseId));
              }

              // Conditional dispatch for LessonSteps
              if (!stepsLoaded) {
                // If lessons were not loaded, ClearAllLessons would have already cleared steps.
                // If lessons WERE loaded but steps were NOT, then we definitely need to fetch steps.
                // No explicit clear for steps here, relying on ClearAllLessons if lessons also needed loading.
                this.store$.dispatch(new GetAllLessonSteps(courseId));
              }
            }),
            map(([dispatchAction, _workbooksLoaded, _lessonsLoaded, _stepsLoaded]) => {
              return new actions.SetCurriculumCourseSuccess(dispatchAction.payload);
            })
          );
        })
    );
  });

  SetCurriculumCourseSuccess$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(actions.SetCourseActionTypes.SetCurriculumCourseSuccess),
        map((action: actions.SetCurriculumCourseSuccess) => action.payload),
        tap((payload) => {
          console.log('--- SETTING CURRICULUM COURSE SUCCESS ---')
          localStorage.setItem('previousSelectedCourse', JSON.stringify(payload.course));
          if (payload.redirect) {
            this.router.navigateByUrl('/my-curriculum');
          }
        }),
    );
  }, { dispatch: false });


  setCurriculumCourseById$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(actions.SetCourseActionTypes.SetCurriculumCourseById),
        switchMap((action: any) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(selectCourseData, { id: action.payload })
            ),
            tap(([action, courseData]) => {
              if (courseData) {
                this.store.dispatch(new actions.SetCurriculumCourse({
                  course: courseData
                }));
              }
            })
          ),
        )
    );
  }, {dispatch: false});
}
